import { ComponentLoader } from '@/components/ComponentLoader';
import { StepLayout } from '@/components/NewGeneralDialog/StepLayout';
import { DOCUMENT_STATUS } from '@/constants/documents';
import { useGetDocumentById } from '@/hooks/Documents/useGetDocumentById';
import { Box, Button, Stack, Typography } from '@mui/material';
import { usePollingUntilReady } from '@treyd-io/core/hooks/usePollingUntilReady';
import { toSentenceCase } from '@treyd-io/core/utils/string';
import ArrowRightIcon from '@treyd-io/treyd-ui/icons/ArrowRightIcon';
import { toNumber } from 'lodash';
import { useSession } from 'next-auth/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const PdfPreviewStep = ({
  documentId,
  isGeneratingDocument,
  onMovingForward,
}: {
  documentId?: number;
  isGeneratingDocument: boolean;
  onMovingForward: () => void;
}) => {
  const { t } = useTranslation();
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const { data: session, status } = useSession({ required: true });
  const companyId = session?.user?.company_id;

  const {
    data: documentData,
    refetch,
    isPending: isLoadingCurrentDocument,
  } = useGetDocumentById({
    documentId: toNumber(documentId),
    companyId: toNumber(companyId),
  });

  const currentDocument = documentData?.data;
  const isPdfReady = Boolean(
    currentDocument?.pdf_url &&
      currentDocument?.status === DOCUMENT_STATUS.PENDING_SIGNATURE
  );

  const isLoading =
    status === 'loading' ||
    isLoadingCurrentDocument ||
    isGeneratingDocument ||
    (!isPdfReady && !hasTimedOut);

  usePollingUntilReady({
    isReady: isPdfReady,
    pollFn: () => refetch(),
    onTimeout: () => setHasTimedOut(true),
  });

  if (isLoading) {
    return <ComponentLoader />;
  }

  return (
    <Box maxWidth={960} margin="auto" marginBottom={6}>
      <StepLayout>
        <Stack gap={3}>
          <Box>
            <Typography variant="h4" color="text.primary" marginBottom={1}>
              {t('review_document')}
            </Typography>
            <Typography variant="body1" color="text.primary">
              {t(
                'this_is_the_finalized_document_please_review_it_carefully_and_proceed_to_sign_when_ready'
              )}
            </Typography>
          </Box>

          {isPdfReady ? (
            <iframe
              src={currentDocument?.pdf_url}
              title={toSentenceCase(
                currentDocument?.title?.toLowerCase() || ''
              )}
              style={{ width: '100%', height: '100vh' }}
            />
          ) : (
            hasTimedOut && (
              <PdfHasTimedOut
                onRetry={() => {
                  refetch();
                  setHasTimedOut(false);
                }}
              />
            )
          )}

          <Button
            variant="contained"
            color="primary"
            size="large"
            endIcon={<ArrowRightIcon />}
            sx={{ marginTop: 0.5, alignSelf: { sm: 'flex-start' } }}
            onClick={onMovingForward}
            disabled={isLoading}>
            {t('sign_document')}
          </Button>
        </Stack>
      </StepLayout>
    </Box>
  );
};

const PdfHasTimedOut = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation();
  return (
    <Stack
      justifyContent={'center'}
      alignItems={'center'}
      gap={2}
      paddingY={5}
      sx={{
        border: 1,
        borderColor: 'other.outlined_border',
        borderRadius: 2,
        boxShadow: 'none',
        backgroundColor: 'background.default',
      }}>
      <Typography variant="body2" color="text.secondary">
        {t(
          'the_document_couldnt_be_loaded_at_the_moment_you_can_still_proceed_with_signing_and_review_it_later_or_try_again'
        )}
      </Typography>
      <Button
        variant="outlined"
        color="secondary"
        size="medium"
        onClick={onRetry}>
        {t('try_again')}
      </Button>
    </Stack>
  );
};
